// src/components/kanban/TaskCardView.tsx
/**
 * Card presentazionale di un task Kanban (Planner-style): copertina opzionale, check
 * rapido completa/riapri, priorità, scadenza/scostamento, checklist/allegati, assegnatari.
 * Nessun hook dnd-kit qui: il drag è delegato a SortableTaskCard, che passa
 * `dragHandleProps` da spargere sull'intera card (drag sull'intera superficie).
 */
import { useState } from 'react';
import { ActionIcon, Badge, Group, Image, Menu, Paper, Stack, Text, Tooltip } from '@mantine/core';
import {
  IconAlertTriangle,
  IconCalendar,
  IconCheck,
  IconChecklist,
  IconDotsVertical,
  IconPaperclip,
  IconTrash,
} from '@tabler/icons-react';
import dayjs from 'dayjs';
import type { ProjectTaskSummary } from '../../types/api';
import { diffInDays } from '../../utils/date-utils';
import AssigneeAvatars from './AssigneeAvatars';

export interface TaskCardViewProps {
  task: ProjectTaskSummary;
  showCover: boolean;
  /** true quando la card è renderizzata nella sezione "Completate" di una colonna. */
  completed?: boolean;
  /** true quando la card è renderizzata dentro il DragOverlay (puramente visiva). */
  overlay?: boolean;
  onClick?: () => void;
  onToggleComplete?: () => void;
  onDelete?: () => void;
  /** Spread dal wrapper sortable sull'intera card (drag sull'intera superficie). */
  dragHandleProps?: { attributes?: any; listeners?: any };
}

const PRIORITY_COLORS: Record<string, string> = {
  Low: 'gray',
  Medium: 'blue',
  High: 'orange',
  Critical: 'red',
};

const PRIORITY_LABELS: Record<string, string> = {
  Low: 'Bassa',
  Medium: 'Media',
  High: 'Alta',
  Critical: 'Critica',
};

export default function TaskCardView({
  task,
  showCover,
  completed = false,
  overlay = false,
  onClick,
  onToggleComplete,
  onDelete,
  dragHandleProps,
}: TaskCardViewProps) {
  const [coverError, setCoverError] = useState(false);

  const priorityColor = PRIORITY_COLORS[task.priorityString] ?? 'gray';
  const priorityLabel = PRIORITY_LABELS[task.priorityString] ?? task.priorityString;
  const completedDelta = completed ? diffInDays(task.completedAt, task.dueDate) : null;

  const dragProps = dragHandleProps
    ? { ...(dragHandleProps.attributes ?? {}), ...(dragHandleProps.listeners ?? {}) }
    : {};

  return (
    <Paper
      withBorder
      radius="md"
      p={0}
      onClick={overlay ? undefined : onClick}
      style={{
        overflow: 'hidden',
        cursor: overlay ? 'default' : onClick ? 'pointer' : undefined,
        boxShadow: overlay ? '0 12px 24px rgba(0, 0, 0, 0.18)' : undefined,
        transform: overlay ? 'rotate(2deg)' : undefined,
      }}
      {...dragProps}
    >
      {showCover && task.coverThumbnailUrl && !coverError && (
        <Image
          src={task.coverThumbnailUrl}
          h={120}
          fit="cover"
          loading="lazy"
          onError={() => setCoverError(true)}
          style={completed ? { opacity: 0.6 } : undefined}
        />
      )}

      <Stack gap={6} p="sm">
        <Group justify="space-between" wrap="nowrap" gap="xs">
          <Group gap="xs" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            {!overlay && onToggleComplete && (
              <Tooltip label={completed ? 'Riapri task' : 'Segna come completato'}>
                <ActionIcon
                  size="sm"
                  radius="xl"
                  variant={completed ? 'filled' : 'light'}
                  color="green"
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleComplete();
                  }}
                >
                  <IconCheck size={14} />
                </ActionIcon>
              </Tooltip>
            )}
            <Text
              fw={500}
              size="sm"
              lineClamp={2}
              td={completed ? 'line-through' : undefined}
              c={completed ? 'dimmed' : undefined}
              style={{ flex: 1 }}
            >
              {task.title}
            </Text>
          </Group>

          {!overlay && onDelete && (
            <Menu position="bottom-end" withinPortal>
              <Menu.Target>
                <ActionIcon variant="subtle" size="sm" onClick={(e) => e.stopPropagation()}>
                  <IconDotsVertical size={14} />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown onClick={(e) => e.stopPropagation()}>
                <Menu.Item color="red" leftSection={<IconTrash size={14} />} onClick={onDelete}>
                  Elimina
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          )}
        </Group>

        {(task.requestLabel || task.clientName) && (
          <Text size="xs" c="dimmed" lineClamp={1}>
            {task.requestLabel}
            {task.clientName ? ` · ${task.clientName}` : ''}
          </Text>
        )}

        {task.description && (
          <Text size="xs" c="dimmed" lineClamp={2}>
            {task.description}
          </Text>
        )}

        <Group gap={6}>
          <Badge size="sm" variant="light" color={priorityColor}>
            {priorityLabel}
          </Badge>
        </Group>

        {!completed && task.dueDate && (
          <Tooltip label="Data prevista">
            <Group gap={4}>
              {task.isOverdue ? (
                <IconAlertTriangle size={14} color="#fa5252" />
              ) : (
                <IconCalendar size={14} color="#868e96" />
              )}
              <Text size="xs" c={task.isOverdue ? 'red' : 'dimmed'}>
                {dayjs(task.dueDate).format('DD/MM/YYYY')}
              </Text>
            </Group>
          </Tooltip>
        )}

        {completed && task.completedAt && (
          <Group gap={6}>
            <Text size="xs" c="dimmed">
              Completato il {dayjs(task.completedAt).format('DD/MM/YYYY')}
            </Text>
            {task.dueDate && completedDelta != null && (
              <Badge size="xs" variant="light" color={completedDelta <= 0 ? 'green' : 'red'}>
                {completedDelta <= 0 ? 'In tempo' : `+${completedDelta} gg`}
              </Badge>
            )}
          </Group>
        )}

        <Group justify="space-between" gap="xs">
          <Group gap="sm">
            {task.checklistItemsCount > 0 && (
              <Group gap={4}>
                <IconChecklist size={14} color="#868e96" />
                <Text size="xs" c="dimmed">
                  {task.completedChecklistItemsCount}/{task.checklistItemsCount}
                </Text>
              </Group>
            )}
            {task.attachmentsCount > 0 && (
              <Group gap={4}>
                <IconPaperclip size={14} color="#868e96" />
                <Text size="xs" c="dimmed">
                  {task.attachmentsCount}
                </Text>
              </Group>
            )}
          </Group>
          <AssigneeAvatars assignees={task.assignees} />
        </Group>
      </Stack>
    </Paper>
  );
}
